import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Option, pipe, Record as R } from 'effect';

import { documentsApi } from '@/domain/documents/api';
import type { DocumentOcrExtractedFieldBox, DocumentOcrPage, DocumentOcrWord } from '@/domain/documents/schemas';
import type { EndpointResponse } from '@/services/api';
import { queryOptions } from '@/services/query';

import { documentsKeys } from '@/domain/documents/queries/keys';

/** The id is the word's place on its page, so a refetch of the same map gives the same ids. */
export type OcrTextMapWord = DocumentOcrWord & { id: string };

/** Sizes and boxes are in OCR units, not pixels of the displayed page. */
export type OcrTextMapPage = {
  width: number;
  height: number;
  words: OcrTextMapWord[];
  extractedFields: Record<string, DocumentOcrExtractedFieldBox>;
};

/** Keyed by page number ("1", "2", …); a page without OCR has no entry. */
export type OcrTextMap = Record<string, OcrTextMapPage>;

const ocrTextMapPage = (page: DocumentOcrPage, pageNumber: string): OcrTextMapPage => ({
  width: Number(page.width),
  height: Number(page.height),
  words: page.data.map((word, index) => ({ ...word, id: `${pageNumber}-${index}` })),
  extractedFields: R.filterMap(page.coordinates ?? {}, Option.fromNullable),
});

export const ocrTextMapFromResponse = (response: EndpointResponse<typeof documentsApi.getOcrTextMap>): OcrTextMap =>
  R.filterMap(response, (page, pageNumber) =>
    pipe(
      Option.fromNullable(page),
      Option.map((parsed) => ocrTextMapPage(parsed, pageNumber)),
    ),
  );

export const documentOcrTextMapQuery = <TData = OcrTextMap>(
  companyId: string,
  documentId: string,
  select?: (map: OcrTextMap) => TData,
) =>
  queryOptions({
    queryKey: documentsKeys.ocrTextMap(companyId, documentId),
    // Mapped here, not in `select`: the cache keeps plain objects, so a refetch that brings the same map
    // keeps every page's reference and re-renders nothing.
    queryFn: () => documentsApi.getOcrTextMap({ query: { documentID: documentId } }).then(ocrTextMapFromResponse),
    // Fetched once while the document page is open: a reader mounting later must not refetch it, and the cache
    // goes when the page is left, so the next open of the document fetches a fresh map.
    staleTime: Infinity,
    cacheTime: 0,
    select,
  });

/** Unique word texts of the whole document, in page and word order. */
export const documentOcrWordTexts = (map: OcrTextMap): string[] => [
  ...new Set(Object.values(map).flatMap((page) => page.words.map((word) => word.text))),
];

export const documentHasExtractedFields = (map: OcrTextMap) =>
  Object.values(map).some((page) => Object.keys(page.extractedFields).length > 0);

export const useDocumentOcrTextMap = <TData = OcrTextMap>(documentId: string, select?: (map: OcrTextMap) => TData) => {
  const { companyId } = useParams();
  return useQuery({
    ...documentOcrTextMapQuery(companyId ?? '', documentId, select),
    enabled: Boolean(companyId) && Boolean(documentId),
  });
};

/** One page of the map; `undefined` for a page OCR did not cover. */
export const useDocumentOcrTextMapPage = (documentId: string, pageNumber: number) => {
  const selectPage = useCallback(
    (map: OcrTextMap): OcrTextMapPage | undefined => map[String(pageNumber)],
    [pageNumber],
  );
  return useDocumentOcrTextMap(documentId, selectPage);
};
